import React, { lazy, useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ThemeProvider } from "components/ui";
import { CookiesProvider } from "react-cookie";
import { ConfigProvider } from "antd";
import { Provider as MotionProvider } from "rc-motion";

import AppNotifications from "components/AppNotifications/AppNotifications";
import ConnectionBanner from "components/ConnectionBanner/ConnectionBanner";
import ProjectsProvider from "contexts/Projects";
import FetchProvider from "contexts/FetchProvider/FetchProvider";
import DevToolsProvider from "contexts/DevTools";
import PageTitles from "components/PageTitles";
import Routes from "./Routes";
import TenantProvider from "./contexts/Tenant";
import AuthProvider from "./contexts/Auth";
import OrganizationsProvider from "./contexts/Organizations";
import { ToasterProvider } from "./contexts/Toaster";
import { ModalProvider } from "./contexts/Modal";
import { FullpageSpinner } from "./components";
import { AppGlobalStyle } from "./styles/AppGlobalStyle";
import hegiasTheme from "./styles/hegiasTheme";
import antdTheme from "./styles/antdTheme";
import usePrefersReducedMotion from "./hooks/usePrefersReducedMotion";
import ErrorBoundary from "./components/ErrorBoundary";
import installTouchDragBridge from "./utils/touchDragBridge";

import "./styles/index.css";

const WelcomeModal = lazy(() => import("./views/WelcomePopup"));

const App = () => (
	<ErrorBoundary>
		<Router basename={process.env.PUBLIC_URL}>
			<DevToolsProvider>
				<ReducedMotion>
					<Providers>
						<AppGlobalStyle />
						<TouchDragBridge />
						<PageTitles />
						<Routes />
						<WelcomeModal />
					</Providers>
				</ReducedMotion>
			</DevToolsProvider>
		</Router>
	</ErrorBoundary>
);

/**
 * Turns antd's popup animations off outright when the viewer asked for reduced
 * motion, instead of merely shortening them.
 *
 * This is a correctness fix, not a preference. `components/ui`'s GlobalStyles —
 * mounted app-wide by ThemeProvider below — carries the common accessibility
 * reset `@media (prefers-reduced-motion: reduce) { * { animation-duration:
 * 0.01ms !important } }`. antd's popups leave their `display: none` state
 * through rc-motion's CSS-animation lifecycle, and at 0.01ms that lifecycle
 * finishes before `@rc-component/trigger` has measured the popup. It then aligns
 * a 0x0 box, and its zero-size guard misses because `parseFloat("auto")` on a
 * display:none element yields NaN, which rc-trigger's `toNum` turns into 1
 * rather than 0. The popup lands thousands of pixels off-screen and every
 * dropdown in the app looks dead.
 *
 * `motion={false}` makes rc-motion skip the animation entirely (its
 * `isSupportTransition` gate), so the popup is measured while it is actually
 * visible. antd's own `theme.token.motion: false` does NOT do this — it only
 * sets the duration tokens to 0s, which reproduces the bug rather than fixing
 * it.
 *
 * Reported by a customer on Edge inside Citrix, where Windows animations are
 * routinely off; reproducible anywhere via DevTools > Rendering > "Emulate CSS
 * media feature prefers-reduced-motion".
 */
/** Finger drag-and-drop for every draggable — see utils/touchDragBridge. */
const TouchDragBridge = () => {
	useEffect(() => installTouchDragBridge(), []);
	return null;
};

const ReducedMotion: React.FC<React.PropsWithChildren> = ({ children }) => {
	const prefersReducedMotion = usePrefersReducedMotion();

	return (
		<MotionProvider motion={!prefersReducedMotion}>{children}</MotionProvider>
	);
};

/**
 * Theme before data, deliberately.
 *
 * TenantProvider, AuthProvider, OrganizationsProvider and ProjectsProvider each
 * block the app until their request resolves, and each renders an error state
 * when it doesn't. With the theme mounted below them, those error states had no
 * design system to draw with - which is why a backend outage used to surface as
 * unstyled text on a yellow background.
 *
 * ConfigProvider and ThemeProvider take static themes and depend on nothing
 * beneath them, so hoisting them is safe. If per-tenant branding ever arrives,
 * this ordering needs revisiting: probably a static shell theme here plus a
 * tenant override mounted below TenantProvider.
 */
const Providers: React.FC<React.PropsWithChildren> = ({ children }) => (
	<CookiesProvider>
		<FetchProvider>
			<ConfigProvider theme={antdTheme}>
				<ThemeProvider theme={hegiasTheme}>
					<TenantProvider>
						<AuthProvider>
							<OrganizationsProvider>
								<ProjectsProvider>
									<ToasterProvider>
										<ModalProvider>
											<React.Suspense
												fallback={<FullpageSpinner />}
											>
												{children}
												<AppNotifications />
												<ConnectionBanner />
											</React.Suspense>
										</ModalProvider>
									</ToasterProvider>
								</ProjectsProvider>
							</OrganizationsProvider>
						</AuthProvider>
					</TenantProvider>
				</ThemeProvider>
			</ConfigProvider>
		</FetchProvider>
	</CookiesProvider>
);

export default App;
